import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import { graphql } from 'gatsby';

import { Section } from '~components/Section';
import {
  renderHeading,
  renderHyperlink,
  renderListItem,
  renderOlList,
  renderParagraph,
  renderUlList,
  RichTextRenderer,
} from '~components/RichTextRenderer';
import { Button } from '~components/Button';
import { ComponentLink } from '~components/ComponentLink';
import { Image } from '~components/Image';
import { tildeToSoftHyphen } from '~utils/string';
import { ComponentCoverageLandingHeaderUspItem } from './ComponentCoverageLandingHeaderUspItem';
import Video from '../../routes/Landing/components/Video';
import * as styles from './ComponentCoverageLandingHeader.module.css';

import type { ContentBlock } from '~components/types';
import type { ContentfulComponentCoverageLandingHeader } from './types';

type ComponentCoverageLandingHeaderProps = {
  content: ContentfulComponentCoverageLandingHeader;
};

export const ComponentCoverageLandingHeader: ContentBlock<ComponentCoverageLandingHeaderProps> = ({
  content: {
    headerContent,
    headerBackground,
    callToActionTarget,
    callToActionText,
    priceIndicator,
    conditions,
    uspItems,
  },
  ...sectionProps
}) => (
  <Section {...sectionProps} className={styles.section} blockLayout='boxed'>
    <div className={styles.text}>
      <div className={styles.body}>
        <RichTextRenderer
          content={tildeToSoftHyphen(headerContent.raw)}
          options={{
            renderNode: {
              [BLOCKS.HEADING_1]: renderHeading(1, 1, { className: styles.h1 }),
              [BLOCKS.HEADING_2]: renderHeading(2, 2),
              [BLOCKS.PARAGRAPH]: renderParagraph({ className: styles.p }),
              [BLOCKS.UL_LIST]: renderUlList(),
              [BLOCKS.OL_LIST]: renderOlList(),
              [BLOCKS.LIST_ITEM]: renderListItem(),
              [INLINES.HYPERLINK]: renderHyperlink(),
            },
            renderMark: {
              [MARKS.ITALIC]: (children) => children,
            },
          }}
        />
        <div className={styles.buttonAndPrice}>
          {callToActionText && callToActionTarget && (
            <Button
              className={styles.ctaButton}
              element={
                <ComponentLink
                  link={{
                    __typename: 'ContentfulComponentLink',
                    contentful_id: '',
                    label: callToActionText,
                    to: callToActionTarget,
                  }}
                />
              }
            >
              {callToActionText}
            </Button>
          )}
          {priceIndicator && (
            <span className={styles.priceIndicator} dangerouslySetInnerHTML={{ __html: priceIndicator }} />
          )}
        </div>
        {conditions && (
          <RichTextRenderer
            content={tildeToSoftHyphen(conditions.raw)}
            wrapperElement='div'
            className={styles.conditions}
          />
        )}
      </div>
      {uspItems && (
        <div className={styles.uspItems}>
          {uspItems.map((item, index) => (
            <ComponentCoverageLandingHeaderUspItem key={index} content={item} />
          ))}
        </div>
      )}
    </div>
    <div className={styles.mediaWrapper}>
      {'content' in headerBackground ? (
        <Image image={headerBackground.content} className={styles.image} />
      ) : (
        headerBackground.url && <Video className={styles.video} content={{ url: headerBackground.url }} />
      )}
    </div>
  </Section>
);

export const query = graphql`
  fragment ComponentCoverageLandingHeader on ContentfulComponentCoverageLandingHeader {
    __typename
    id
    contentfulid
    headerContent {
      raw
    }
    priceIndicator
    conditions {
      raw
    }
    headerBackground {
      ... on Node {
        ... on ContentfulComponentSharedVideo {
          url
        }
        ... on ContentfulComponentSharedImage {
          content {
            ...MixedMedia
          }
        }
      }
    }
    callToActionTarget
    callToActionText
    theme
    uspItems {
      ... on Node {
        ... on ContentfulComponentTrustpilot {
          ...ComponentTrustpilot
        }
        ... on ContentfulComponentCoverageLandingHeaderUspItem {
          ...ComponentCoverageLandingHeaderUspItem
        }
      }
    }
  }
`;
